import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { authAPI } from '../services/api'
import { useBranding } from '../context/BrandingContext'
import { AuthShell, webmailInboxUrl } from './authShared'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RESEND_SECONDS = 30

export default function ForgotPassword() {
  const branding = useBranding()
  const [step, setStep] = useState('request') // 'request' | 'sent'
  const [email, setEmail] = useState('')
  const [sentEmail, setSentEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendIn, setResendIn] = useState(RESEND_SECONDS)
  const [resending, setResending] = useState(false)
  const [resendKey, setResendKey] = useState(0) // bump to (re)start the cooldown
  const tickRef = useRef(null)

  // Resend cooldown — (re)starts each time resendKey changes (entering the "sent"
  // step, or after a resend). Self-clears at zero; bumping the key starts it again.
  useEffect(() => {
    if (step !== 'sent' || resendKey === 0) return
    setResendIn(RESEND_SECONDS)
    tickRef.current = setInterval(() => {
      setResendIn(n => {
        if (n <= 1) { clearInterval(tickRef.current); return 0 }
        return n - 1
      })
    }, 1000)
    return () => clearInterval(tickRef.current)
  }, [step, resendKey])

  async function sendLink(target) {
    // The backend always responds the same way (no account enumeration); only
    // network/validation issues surface as errors.
    await authAPI.forgotPassword(target)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const val = email.trim()
    if (!EMAIL_RE.test(val)) {
      setError(val ? 'Enter a valid email address.' : 'Email is required.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await sendLink(val)
      setSentEmail(val)
      setStep('sent')
      setResendKey(k => k + 1)
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setResending(true)
    try {
      await sendLink(sentEmail)
    } catch {
      /* keep the screen quiet — the cooldown restarts regardless */
    } finally {
      setResending(false)
      setResendKey(k => k + 1)
    }
  }

  function changeEmail() {
    clearInterval(tickRef.current)
    setStep('request')
  }

  return (
    <AuthShell>
      {step === 'request' ? (
        <div className="step anim-in" key="request">
          <div className="form-medallion">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="13.5" r="4.5"/><path d="m11 12 9-9M17 6l2 2M14 9l2 2"/></svg>
          </div>
          <div className="form-head">
            <h1>Forgot your password?</h1>
            <p>Enter the email tied to your {branding.cmsName} workspace and we'll send you a link to reset it.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate style={{ marginTop: 26 }}>
            <div className={`field${error ? ' has-error' : ''}`}>
              <div className="field-label"><label htmlFor="fp-email">Work email</label></div>
              <div className="input-wrap">
                <span className="lead-ico">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg>
                </span>
                <input
                  className="ds-input with-ico"
                  type="email"
                  id="fp-email"
                  name="email"
                  placeholder="you@studio.com"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (error) setError('') }}
                />
              </div>
              <div className="field-error">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.01"/></svg>
                <span>{error}</span>
              </div>
            </div>

            <button className="ds-btn ds-btn-primary btn-submit" type="submit" disabled={loading} style={{ opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Sending link…' : 'Send reset link'}
              {!loading && <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>}
            </button>
          </form>

          <div className="help-list">
            <h4>Before you ask for a link</h4>
            <ul>
              <li>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10"/></svg>
                Use the same email you sign in with — invites and SSO can differ.
              </li>
              <li>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10"/></svg>
                Links expire after 60 minutes for your security.
              </li>
            </ul>
          </div>

          <div className="form-foot">
            Remembered it? <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      ) : (
        <div className="step anim-in" key="sent">
          <div className="form-medallion">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg>
          </div>
          <div className="form-head">
            <h1>Check your inbox</h1>
            <p>If an account exists, we've sent a reset link to <span className="email-pill">{sentEmail}</span>. It's valid for 60 minutes.</p>
          </div>

          <div className="sent-card">
            <div className="env">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 6 8-6"/></svg>
            </div>
            <div className="env-txt">
              Didn't get it? Check your <b>spam</b> or <b>promotions</b> folder, and confirm the address above is correct.
            </div>
          </div>

          <button
            className="ds-btn ds-btn-primary btn-submit"
            type="button"
            style={{ marginTop: 18 }}
            onClick={() => window.open(webmailInboxUrl(sentEmail), '_blank', 'noopener')}
          >
            Open email app
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8"/></svg>
          </button>

          <div className="resend-row">
            {resendIn > 0
              ? <span>Resend available in <b>{resendIn}</b>s</span>
              : <button type="button" onClick={handleResend} disabled={resending}>{resending ? 'Sending…' : 'Resend link'}</button>}
          </div>

          <div className="form-foot">
            Wrong address? <button type="button" onClick={changeEmail}>Use a different email</button>
          </div>
        </div>
      )}
    </AuthShell>
  )
}
